import { BonusType } from '@/webgl/runner/entities/BonusEntity'
import { GateTrackConfig } from '@/webgl/runner/scenarios/tracks/GateTrack'
import { ProceduralTrackConfig } from '@/webgl/runner/scenarios/tracks/ProceduralTrack'
import { ShoppingBalanceTrackConfig } from '@/webgl/runner/scenarios/tracks/ShoppingBalanceTrack'

/** Control the behavior of a playing sequence */
export type SequenceConfig = {
  id: string

  /** Specific rules override for the sequence */
  alternativeMode?: SequenceAlternativeMode

  /** Procedural track config */
  track: TrackConfig

  /** Scrolling background items */
  scrolling: string[]

  /** UI Information */
  view: SequenceView

  /** Playable audio for good or wrong collect status */
  audio?: {
    music?: string
    good?: string[]
    wrong?: string[]
  }

  /** Config when sequence start */
  start?: {
    /** Play an audio when start */
    audio?: string

    /** Play audios when start, but keeps playing in the background */
    backgroundAudios?: {
      audio: string
      delay?: number
    }[]

    /** Alter the jauge with number value, alter add the value (can be negative), set, set the value */
    alterJauge?: number
    setJauge?: number

    /** Add bonus to the bonus list when sequence start */
    bonus?: BonusType

    /** Start the sequence with a barrier */
    barrier?: string
  }

  /** Config when sequence is complete */
  stop?: {
    /** Play an audio before leaving */
    audio?: string

    /** Alter the jauge with number value */
    alterJauge?: number

    /** Dont clear bonus from active bonus list */
    preserveBonus?: boolean

    /** Dont remove collectible from lanes */
    preserveCollectibles?: boolean

    /** Keep the completion state, collected items */
    preserveCompletion?: boolean
  }

  /** Jauge increase rate when missing a goal */
  jaugeRate?: number

  /** Goals to complete the sequence */
  goals: string[][]

  /** Goal Text above the icons */
  goalText?: string

  /** Boolean to change Goal Text color */
  goalTextDM?: boolean

  /** Initialize with predefined goal already collected, usefull for seamless transition between sequences */
  collected?: string[][]

  /** Condition to trigger the completion of the segment */
  condition?: CompleteCondition

  /** What to do when play segment is completed */
  resolution?: ResolutionAction
}

export type TrackConfig = ProceduralTrackConfig | GateTrackConfig | ShoppingBalanceTrackConfig

export enum SequenceAlternativeMode {
  /** Shopping list to be completed in any order*/
  Shopping = 'shopping',
  /** Remove items completion based on shop.wallet value*/
  ShoppingBalance = 'shopping-balance',
  /** Unordered goal list */
  Unordered = 'unordered',
}

export type SequenceView = {
  variant: SequenceViewVariant
  template?: string
  unordered?: boolean
  suffix?: string
  replacement?: Record<string, { goal: string; inverted: string }>
  extra?: SequenceViewExtra
}

export type SocialStudiesPlayIntro = {
  id: 'social-studies-play-intro'
}

export type SequenceViewExtra = SocialStudiesPlayIntro

export enum SequenceViewVariant {
  Default = 'default',
  Equation = 'equation',
  Hidden = 'hidden',
  Bubble = 'bubble',
  InvertLetters = 'invert-letters',
  Syllable = 'sillable',
  Dictation = 'dictation',
  Shopping = 'shopping',
}

/** Conditions override to resolve complete the sequence  */
export type CompleteCondition =
  GrabCountCondition | CollectedCountCondition | SpawnedCountCondition | BlockSequenceCondition

/** End the sequence after gabbing a fixed number of collectible (good or wrong) */
export type GrabCountCondition = {
  id: 'grab-count'
  count: number
}

/** End the sequence after collected a good collectible number */
export type CollectedCountCondition = {
  id: 'collected-count'
  count: number
}

/** End the sequence after a fixed number of collectible as been spawned */
export type SpawnedCountCondition = {
  id: 'spawned-count'
  count: number
}

/** Fail and end the sequence if wrong collectible is grabbed */
export type BlockSequenceCondition = {
  id: 'blocks'
}

/** Extra action to trigger when complete condition is met */
export type ResolutionAction = BarrierResolution | DelayResolution

export type BarrierResolution = {
  id: 'barrier'
  barrier: string
}

/** Complete sequence after delay */
export type DelayResolution = {
  id: 'delay'
  duration: number
}
